import { every } from 'lodash-es';
import { useEffect, useRef, type FocusEvent } from 'react';

import { useApplyPanDelta } from './hooks/useApplyPanDelta.js';
import { useApplyZoomDelta } from './hooks/useApplyZoomDelta.js';
import { useResetInteractions } from './hooks/useResetInteractions.js';
import { useResetOnResize } from './hooks/useResetOnResize.js';
import { TimeseriesCrosshair } from './TimeseriesExplore/TimeseriesCrosshair.js';
import { TimeseriesInspect } from './TimeseriesExplore/TimeseriesInspect.js';
import { TimeseriesSelectionArea } from './TimeseriesSelection/TimeseriesSelectionArea.js';
import { getPointsParsed } from './utils/get-points-parsed.js';
import { EMPTY_DATAPOINT } from './utils/tooltip/constants.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { InteractionsOverlay } from '../../../core/components/interactions-overlay/InteractionsOverlay.js';
import {
  INTERACTION_MODE,
  type InteractionMode,
  type InteractionTriggers,
  isSelectCancelled,
  type ZoomBehavior,
} from '../../../core/components/interactions-overlay/types.js';
import { isOwnEvent } from '../../../core/components/interactions-overlay/utils/is-own-event.js';
import {
  reduceExploreCancelAll,
  reduceExploreCancelSelect,
  reduceExploreCancelTooltip,
  reduceExploreEnd,
  reduceExplorePin,
  reduceExplorePosition,
  reduceExploreReset,
  reduceExploreSelect,
  reduceExploreUnPin,
} from '../../../core/components/interactions-state-helpers/reducers/explore-reducers.js';
import { reduceInteractionMode } from '../../../core/components/interactions-state-helpers/reducers/mode-reducers.js';
import type {
  _SelectionRef,
  SelectionAreaVariant,
} from '../../../core/components/selection-area/types.js';
import { SynchronizedSelectionArea } from '../../../core/components/shared-chart-interactions/components/SynchronizedSelectionArea.js';
import { VerticalSynchronizedCrosshair } from '../../../core/components/shared-chart-interactions/components/VerticalSynchronizedCrosshair.js';
import { useSynchronizedCrosshair } from '../../../core/components/shared-chart-interactions/hooks/useSynchronizedCrosshair.js';
import { useSynchronizedSelection } from '../../../core/components/shared-chart-interactions/hooks/useSynchronizedSelection.js';
import { useToolbarHandlerRef } from '../../../core/components/toolbar/hooks/useToolbarHandlerRef.js';
import type { RelativeAbsolutePosition } from '../../../core/types/pointer-events.js';
import { getPercentageFromPixel } from '../../../core/utils/get-percentage-from-pixel.js';
import {
  TIMESERIES_INTERACTIONS_ARIA_LABEL,
  zIndicesTimeseriesAreas,
} from '../../constants.js';
import { TimeseriesChartOverlayRefContext } from '../../context/TimeseriesChartOverlayRefContext.js';
import { TimeseriesChartSelectionAreaRef } from '../../context/TimeseriesChartSelectionAreaRef.context.js';
import {
  useExploreState,
  useInteractionModeState,
  useSelectState,
  useSetState,
  useZoomState,
} from '../../hooks/store/selectors.js';
import { useZoomToSelection } from '../../hooks/store/state-setters.js';
import { useChartInteractions } from '../../hooks/useChartInteractions.js';
import { useChartToolbar } from '../../hooks/useChartToolbar.js';
import { useTimeseriesChartId } from '../../hooks/useTimeseriesChartId.js';
import { useTimeseriesFilteredData } from '../../hooks/useTimeseriesFilteredData.js';
import { useXScales } from '../../hooks/useXScale.js';
import { useYScales } from '../../hooks/useYScales.js';
import { TimeseriesChartToolbar } from '../toolbar/TimeseriesChartToolbar.js';

export const TimeseriesChartOverlays = () => {
  const handlerRef = useToolbarHandlerRef();
  /* eslint-disable-next-line @typescript-eslint/no-deprecated */
  const { zoomX, pan, zoom } = useChartInteractions();
  const selectionRef = useRef<_SelectionRef>(null);

  const { width, height } = useGridPlotSize();

  const toolbar = useChartToolbar();

  const exploreState = useExploreState();
  const selectState = useSelectState();
  const mode = useInteractionModeState();
  const zoomState = useZoomState();

  const setState = useSetState();
  const applyPanDelta = useApplyPanDelta();
  const applyZoomDelta = useApplyZoomDelta();
  const resetInteractions = useResetInteractions();

  useResetOnResize();

  const synchronizedCrosshair = useSynchronizedCrosshair();
  const synchronizedSelection = useSynchronizedSelection();
  const chartId = useTimeseriesChartId();

  const overlayRef = useRef<HTMLDivElement>(null);

  const {
    updateHoverState: updateSynchronizedCrosshair,
    hoveredChartId,
    disabled: synchronizedCrosshairDisabled,
    setDisabled: disableSynchronizedCrosshair,
  } = synchronizedCrosshair ?? {};

  const {
    updateSelectionState: updateSynchronizedSelection,
    resetSelectionState: resetSynchronizedSelection,
    selectedChartId,
    disabled: synchronizedSelectionDisabled,
  } = 'selectedChartId' in synchronizedSelection
    ? synchronizedSelection
    : { disabled: true };

  useEffect(() => {
    if (
      every([
        selectState,
        !synchronizedSelection.selectedChartId,
        !synchronizedSelectionDisabled,
      ])
    ) {
      // clear current chart selection if the shared selection was removed
      setState((s) => ({ ...s, select: undefined }));
    }
  }, [synchronizedSelection.selectedChartId]);

  useEffect(() => {
    if (synchronizedSelection.selectedChartId === chartId && !selectState) {
      // clear shared chart selection if the current selection was removed
      synchronizedSelection.resetSelectionState?.();
    }
  }, [!!selectState]);

  const showVerticalCrosshair =
    hoveredChartId !== chartId && !synchronizedCrosshairDisabled;
  const showSynchronizedSelection =
    selectedChartId !== chartId && !synchronizedSelectionDisabled;

  const { currentScale: xScale } = useXScales();
  const { datapointsMap, allDatapoints } = useTimeseriesFilteredData();
  const yScale = useYScales();
  const { position } = useExploreState();
  const zoomToSelection = useZoomToSelection();

  const { closestPoint, filteredPoints, groupedPoints } = getPointsParsed(
    position?.relative,
    allDatapoints,
    xScale,
    yScale,
    datapointsMap,
    width,
    height,
  );

  const handleModeChange = (event: {
    mode: InteractionMode;
    keyboard: boolean;
  }) => {
    const canPan = zoomState.zoomOutEnabled;
    const shouldSwitchToPanMode =
      event.mode === 'pan' && pan?.enabled && canPan;
    const shouldSwitchToZoomMode =
      event.mode === 'zoom' && (zoomX?.enabled || zoom?.enabled);

    if (shouldSwitchToPanMode || shouldSwitchToZoomMode) {
      setState(reduceInteractionMode(event.mode));
      setState(reduceExploreReset);
      resetSynchronizedSelection?.();
    } else if (event.mode === 'explore') {
      setState(reduceInteractionMode(INTERACTION_MODE.Explore));
    }
  };

  const handleHover = (payload: {
    triggeredBy: InteractionTriggers;
    position: RelativeAbsolutePosition;
  }) => {
    if (mode === 'explore') {
      setState(reduceExplorePosition(payload.position));
    }

    const hoveredDate = xScale.invert(payload.position.relative.x);
    disableSynchronizedCrosshair?.(false);
    updateSynchronizedCrosshair?.(chartId, hoveredDate.valueOf());
  };

  const handleLeave = (_: { triggeredBy: InteractionTriggers }) => {
    if (mode === 'explore') {
      setState(reduceExploreEnd);
    }

    updateSynchronizedCrosshair?.(undefined);
  };

  const handlePanChange = (event: {
    triggeredBy: InteractionTriggers;
    delta: number;
  }) => {
    if (pan?.enabled) {
      applyPanDelta(event.delta);
    }
  };

  const handleTogglePin = (event: {
    triggeredBy: InteractionTriggers;
    position: RelativeAbsolutePosition | undefined;
    pin: boolean;
  }) => {
    const highlightedSeriesId =
      closestPoint?.id && closestPoint.id !== EMPTY_DATAPOINT.id
        ? closestPoint?.id
        : undefined;

    setState(
      event.pin
        ? reduceExplorePin(event.position, highlightedSeriesId)
        : reduceExploreUnPin(event.position),
    );

    updateSynchronizedCrosshair?.(undefined);
  };

  const handleReset = (event: {
    triggeredBy: InteractionTriggers;
    mousePosition?: RelativeAbsolutePosition;
  }) => {
    disableSynchronizedCrosshair?.(false);
    resetSynchronizedSelection?.();
    resetInteractions(event.mousePosition);
  };

  const handleSelectStart = (event: {
    triggeredBy: InteractionTriggers;
    position: RelativeAbsolutePosition;
  }) => {
    if (mode === 'explore') {
      updateSynchronizedSelection?.();
    } else if (!zoomX?.enabled) {
      handleTogglePin({
        triggeredBy: event.triggeredBy,
        position: event.position,
        pin: !exploreState.pinned,
      });
    }

    disableSynchronizedCrosshair?.(true);
  };

  const handleSelectChange = (event: {
    triggeredBy: InteractionTriggers;
    start: RelativeAbsolutePosition;
    end: RelativeAbsolutePosition;
    mode: 'zoom' | 'explore';
  }): void => {
    if (zoomX?.enabled || mode === 'explore') {
      const variant: SelectionAreaVariant =
        event.mode === 'explore' ? 'select' : 'zoom';

      setState(
        reduceExploreSelect({
          start: event.start,
          end: event.end,
          finished: false,
          accessible: false,
          variant,
          selectionPercentage: getPercentageFromPixel(
            [event.start, event.end],
            width,
          ),
        }),
      );

      updateSynchronizedSelection?.({
        selection: [event.start, event.end],
        chartId,
        scale: xScale,
      });
    }
  };

  const handleSelectEnd = (
    event:
      | {
          triggeredBy: InteractionTriggers;
          start: RelativeAbsolutePosition;
          end: RelativeAbsolutePosition;
        }
      | { cancelled: true },
  ): void => {
    if (isSelectCancelled(event)) {
      setState(reduceExploreCancelSelect);
      resetSynchronizedSelection?.();
    } else if (mode === 'explore') {
      setState(
        reduceExploreSelect({
          start: event.start,
          end: event.end,
          finished: true,
          accessible: false,
          selectionPercentage: getPercentageFromPixel(
            [event.start, event.end],
            width,
          ),
        }),
      );
      selectionRef.current?.focus();

      updateSynchronizedSelection?.({
        selection: [event.start, event.end],
        chartId,
        scale: xScale,
      });
    } else if (zoomX?.enabled) {
      // in case select is disabled and zoomX is enabled, only zoom to selection interaction is possible
      zoomToSelection();
    }
  };

  const handleZoomChange = (event: {
    triggeredBy: InteractionTriggers;
    deltaStart: number;
    deltaEnd: number;
    behavior: ZoomBehavior;
  }) => {
    setState(reduceExploreCancelAll);

    applyZoomDelta(event.deltaStart, event.deltaEnd, event.behavior);
  };

  const handleToolbarMouseEnter = () => {
    setState(reduceExploreCancelTooltip);
  };

  const handleFocus = (event?: FocusEvent) => {
    if (!event || event.cancelable) {
      return;
    }
    if (isOwnEvent(event)) {
      handlerRef.current.handleFocus();
    }
  };

  return (
    <InteractionsOverlay
      aria-label={TIMESERIES_INTERACTIONS_ARIA_LABEL}
      style={{
        zIndex: zIndicesTimeseriesAreas.overlays,
        userSelect: selectState?.finished === false ? 'none' : 'auto',
      }}
      ref={overlayRef}
      mode={mode}
      pinned={exploreState.pinned}
      selectionArea={
        selectState ? [selectState?.start, selectState?.end] : undefined
      }
      width={width}
      height={height}
      isZoomDisabled={!zoom?.enabled}
      isZoomXDisabled={!zoomX?.enabled}
      isPanDisabled={!(pan?.enabled && zoomState.zoomOutEnabled)}
      onModeChange={handleModeChange}
      onFocus={handleFocus}
      onHover={handleHover}
      onLeave={handleLeave}
      onPanChange={handlePanChange}
      onTogglePin={handleTogglePin}
      onReset={handleReset}
      onSelectStart={handleSelectStart}
      onZoomChange={handleZoomChange}
      onSelectEnd={handleSelectEnd}
      onSelectChange={handleSelectChange}
    >
      <TimeseriesChartOverlayRefContext.Provider value={overlayRef}>
        {exploreState.visible ? (
          <>
            <TimeseriesInspect
              filteredPoints={filteredPoints}
              closestPoint={closestPoint}
              groupedPoints={groupedPoints}
            />
            <TimeseriesCrosshair closestPoint={closestPoint} />
          </>
        ) : null}
        {showVerticalCrosshair && (
          <VerticalSynchronizedCrosshair scale={xScale} />
        )}

        <TimeseriesChartSelectionAreaRef.Provider value={selectionRef}>
          {toolbar?.enabled ? (
            <TimeseriesChartToolbar
              onMouseEnter={handleToolbarMouseEnter}
              handlerRef={handlerRef}
            />
          ) : null}
          <TimeseriesSelectionArea />
        </TimeseriesChartSelectionAreaRef.Provider>
        {showSynchronizedSelection && (
          <SynchronizedSelectionArea scale={xScale} />
        )}
      </TimeseriesChartOverlayRefContext.Provider>
    </InteractionsOverlay>
  );
};

TimeseriesChartOverlays['displayName'] = 'TimeseriesChartOverlays';
